import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  FilePenLine,
  ListMusic,
  ListPlus,
  MoreHorizontal,
  X,
} from 'lucide-react';
import type { Score } from '@/music/score/types';
import { useStore } from '@/state/store';
import { loadedTuneSource, tuneVersionLabel } from '@/data/tuneSources';
import type { AddTuneToPlaylistControls } from './AddTuneToPlaylist';
import { SetTriggerAutoMark, type SetStripControls } from './SetStrip';
import { Popover } from './Popover';
import { Toast } from './Toast';
import { useSavesToAccount } from '@/data/accountSync';
import { TunePlaybackDefaults } from './TunePlaybackDefaults';
import { PlaylistCallControls } from './PlaylistCallControls';
import { FeedbackLink } from './FeedbackLink';
import { tuneWorkspaceKey } from './tuneWorkspace';
import { touchFirst } from './touchFirst';
import './tuneWorkspace.css';

type LocalSaveState = 'checking' | 'saved' | 'unsaved' | 'unavailable';

async function persistedDocuments() {
  const { name, storage } = useStore.persist.getOptions();
  if (!storage || !name) throw new Error('Local storage is unavailable');
  const state: unknown = (await storage.getItem(name))?.state;
  if (!state || typeof state !== 'object') return undefined;
  return state as { score?: unknown; savedCharts?: unknown };
}

/** Confirm the actual score bytes, not simply that a store action ran. */
function useLocalSaveState(score: Score, copies: readonly Score[]): LocalSaveState {
  const [state, setState] = useState<LocalSaveState>('checking');
  useEffect(() => {
    let current = true;
    setState('checking');
    void persistedDocuments().then(
      (saved) => {
        if (current) {
          setState(JSON.stringify(saved?.score) === JSON.stringify(score) ? 'saved' : 'unsaved');
        }
      },
      () => {
        if (current) setState('unavailable');
      },
    );
    return () => {
      current = false;
    };
  }, [score, copies]);
  return state;
}

function SaveCopyForm({
  name,
  onNameChange,
  onSaved,
}: {
  name: string;
  onNameChange: (name: string) => void;
  onSaved: (message: string) => void;
}) {
  const saveCurrentCopy = useStore((s) => s.saveCurrentCopy);
  const toAccount = useSavesToAccount();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputId = useId();

  return (
    <form
      className="tune-document-form"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!name.trim() || saving) return;
        setSaving(true);
        setError(null);
        try {
          const id = saveCurrentCopy(name.trim());
          const snapshot = useStore.getState().savedCharts.find((copy) => copy.id === id);
          const saved = await persistedDocuments();
          const persisted = Array.isArray(saved?.savedCharts)
            ? saved.savedCharts.find(
                (copy: unknown) =>
                  copy !== null && typeof copy === 'object' && 'id' in copy && copy.id === id,
              )
            : undefined;
          if (!snapshot || JSON.stringify(snapshot) !== JSON.stringify(persisted)) {
            throw new Error('Copy was not written to local storage');
          }
          onSaved(
            toAccount
              ? `“${name.trim()}” saved to your account.`
              : `“${name.trim()}” saved in Saved tunes on this device.`,
          );
        } catch {
          setError(
            'The copy could not be saved on this device. Export a backup to keep your work.',
          );
        } finally {
          setSaving(false);
        }
      }}
    >
      <label htmlFor={inputId}>Copy name</label>
      <input
        id={inputId}
        className="text-input"
        aria-label="Copy name"
        autoFocus
        value={name}
        onChange={(event) => onNameChange(event.target.value)}
      />
      <p className="tune-document-hint">
        Keep a snapshot in Saved tunes. Later edits stay in this workspace.
      </p>
      {error && (
        <p role="alert" className="tune-document-error">
          {error}
        </p>
      )}
      <button className="btn btn-sm btn-primary" type="submit" disabled={!name.trim() || saving}>
        {saving ? 'Saving…' : 'Save copy'}
      </button>
    </form>
  );
}

type TuneMenuView = 'menu' | 'details' | 'copy' | 'playlist';
const TUNE_MENU_LABELS: Record<TuneMenuView, string> = {
  menu: 'Tune',
  details: 'Tune details',
  copy: 'Save a copy',
  playlist: 'Add to playlist',
};

/** Whether a media query matches, following the window as it changes. */
function useMediaMatch(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/** The playlist you are in and where you are in it. A wide header names the
 * playlist; a narrow one has room for the position alone ("2/8") behind a
 * playlist icon — "3/3" by itself did not read as a playlist (Oct 2026) — so
 * the name stays in the text for screen readers and the panel prints it. */
function setTriggerLabel(controls: SetStripControls, compact: boolean, iconOnly = false) {
  return (
    <span
      className={`set-trigger${compact ? ' set-trigger-compact' : ''}${
        iconOnly ? ' set-trigger-icon-only' : ''
      }${controls.onEntry ? '' : ' set-trigger-away'}`}
      data-testid="set-strip"
    >
      {compact && (
        <ListMusic className="set-trigger-icon" size={14} strokeWidth={2} aria-hidden="true" />
      )}
      <span className="set-trigger-name">{controls.name}</span>{' '}
      <span className="set-trigger-position" data-testid="set-strip-position">
        {compact ? controls.shortPosition : controls.position}
      </span>
      {controls.auto && <SetTriggerAutoMark />}
    </span>
  );
}

/** The pill with the set's steps either side: Prev, the pill, Next — one tap
 * to the next tune without opening the panel (Oct 2026). A phone's row has
 * room for Next alone (54px to spare at 375px beside the tabs); Prev stays
 * in the panel there, and on the lock screen. */
function setCluster(controls: SetStripControls, layout: 'wide' | 'narrow' | 'phone') {
  const step = (side: 'prev' | 'next') => {
    const go = side === 'prev' ? controls.stepPrev : controls.stepNext;
    const label = side === 'prev' ? 'Previous tune in the playlist' : controls.nextLabel;
    return (
      <button
        type="button"
        className="btn btn-sm set-cluster-step"
        data-testid={`header-set-${side}`}
        aria-label={label}
        title={`${label}  ${side === 'prev' ? '[' : ']'}`}
        disabled={!go || controls.busy}
        onClick={() => go?.()}
      >
        {side === 'prev' ? (
          <ChevronLeft size={16} aria-hidden="true" />
        ) : (
          <ChevronRight size={16} aria-hidden="true" />
        )}
      </button>
    );
  };
  return (
    <div className="set-cluster" data-testid="set-cluster">
      {layout !== 'phone' && step('prev')}
      <Popover
        label={setTriggerLabel(controls, layout !== 'wide', layout === 'phone')}
        title={controls.title}
        testid="set-strip-trigger"
        align="right"
        overTransport
      >
        {controls.renderPanel}
      </Popover>
      {layout !== 'phone' && step('next')}
    </div>
  );
}

function CompactTuneMenu({
  close,
  closeAndFocus,
  renderDetails,
  onSaved,
  fileActions,
  copyName,
  onCopyNameChange,
  playlistControls,
}: {
  close: () => void;
  closeAndFocus: () => void;
  renderDetails: (close: () => void, inMenu: boolean) => ReactNode;
  onSaved: (message: string) => void;
  fileActions?: (close: () => void) => ReactNode;
  copyName: string;
  onCopyNameChange: (name: string) => void;
  playlistControls?: AddTuneToPlaylistControls;
}) {
  const [view, setView] = useState<TuneMenuView>('menu');
  const itemRefs = useRef<Partial<Record<TuneMenuView, HTMLButtonElement | null>>>({});
  const returnFocusTo = useRef<TuneMenuView | null>(null);
  useLayoutEffect(() => {
    if (view !== 'menu' || !returnFocusTo.current) return;
    const target = itemRefs.current[returnFocusTo.current] ?? itemRefs.current.details;
    target?.focus({ preventScroll: true });
    returnFocusTo.current = null;
  }, [view]);
  const menuItem = (next: Exclude<TuneMenuView, 'menu'>, testid: string, label?: ReactNode) => (
    <button
      ref={(node) => {
        itemRefs.current[next] = node;
      }}
      className="menu-item"
      data-testid={testid}
      onClick={() => setView(next)}
    >
      {label ?? TUNE_MENU_LABELS[next]}
    </button>
  );
  return (
    <div className="tune-menu-content">
      <div className="tune-menu-heading">
        {view !== 'menu' && (
          // A circle, not a worded button: "Back to Tune" beside the view's
          // own title crowded the heading on a phone (Oct 2026).
          <button
            type="button"
            className="close-circle tune-menu-back"
            aria-label="Back to Tune"
            title="Back to Tune"
            onClick={() => {
              returnFocusTo.current = view;
              setView('menu');
            }}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
        )}
        <strong>{TUNE_MENU_LABELS[view]}</strong>
        <button
          type="button"
          className="close-circle tune-menu-close"
          aria-label="Close Tune menu"
          onClick={closeAndFocus}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      {view === 'menu' && (
        <>
          {/* Laid out like Apple Music's "…" menu (user, Oct 2026): Add to
              playlist first — it was last, under Report a tune problem, and
              easy to miss. One row per action, each with its icon. */}
          <div className="tune-menu-list">
            {playlistControls &&
              menuItem(
                'playlist',
                'tune-add-to-playlist',
                <>
                  <ListPlus size={17} aria-hidden="true" /> Add to playlist
                </>,
              )}
            {menuItem(
              'copy',
              'save-tune-copy',
              <>
                <Copy size={17} aria-hidden="true" /> Save a copy
              </>,
            )}
            {menuItem(
              'details',
              'edit-details',
              <>
                <FilePenLine size={17} aria-hidden="true" /> Tune details
              </>,
            )}
            <FeedbackLink kind="tune" className="menu-item" onOpen={close}>
              Report a tune problem
            </FeedbackLink>
          </div>
          {fileActions && <div className="tune-menu-file-actions">{fileActions(close)}</div>}
        </>
      )}
      {view === 'details' && renderDetails(closeAndFocus, true)}
      {view === 'copy' && (
        <SaveCopyForm
          name={copyName}
          onNameChange={onCopyNameChange}
          onSaved={(message) => {
            onSaved(message);
            closeAndFocus();
          }}
        />
      )}
      {view === 'playlist' && playlistControls?.renderPanel(closeAndFocus)}
    </div>
  );
}

/** Same document identity and copy workflow in the Workbench and Score Studio. */
export function TuneDocumentActions({
  details,
  fileActions,
  playbackStatus,
  playlistControls,
  setControls,
}: {
  details?: ReactNode;
  fileActions?: (close: () => void) => ReactNode;
  playbackStatus?: string;
  playlistControls?: AddTuneToPlaylistControls;
  setControls?: SetStripControls | null;
}) {
  const score = useStore((s) => s.score);
  const copies = useStore((s) => s.savedCharts);
  const setChartTitle = useStore((s) => s.setChartTitle);
  const setChartComposer = useStore((s) => s.setChartComposer);
  const status = useLocalSaveState(score, copies);
  const source = loadedTuneSource(score, copies);
  const [editError, setEditError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copyNameDraft, setCopyNameDraft] = useState<string | null>(null);
  const copyName = copyNameDraft ?? `${score.title.trim() || 'Untitled tune'} — copy`;
  const scoreKey = tuneWorkspaceKey(score);
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const composerId = useId();
  const compact = useMediaMatch('(max-width: 700px)');
  // Under 1024px the row has no room for the playlist's name beside the tabs
  // and the actions: on a tablet the tabs ran under Tune details with any
  // playlist open (by 136px at 701 with a long name). The trigger shows
  // where you are in the playlist, as on a phone.
  const shortSet = useMediaMatch('(max-width: 1023px)');

  useEffect(() => {
    setNotice(null);
    setEditError(null);
    setCopyNameDraft(null);
  }, [scoreKey]);

  useEffect(() => {
    if (status === 'saved') setEditError(null);
  }, [status]);

  const edit = (action: () => void) => {
    setNotice(null);
    setEditError(null);
    try {
      action();
    } catch {
      setEditError('This change is visible here but could not be saved on this device.');
    }
  };

  const copySaved = (message: string) => {
    setNotice(message);
    setCopyNameDraft(null);
  };

  // A floating notice says its piece and goes; the header no longer holds it.
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 3200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const closeTuneMenu = (close: () => void) => {
    close();
    rootRef.current
      ?.querySelector<HTMLButtonElement>('[data-testid="tune-menu"]')
      ?.focus({ preventScroll: true });
  };

  const saveState = editError ? 'unsaved' : status;
  const saveWarning = editError
    ? 'Changes are not saved on this device'
    : status === 'unavailable'
      ? 'Local saving is unavailable — export a backup'
      : status === 'saved' || status === 'checking'
        ? null
        : 'Changes are not saved on this device';
  const metadata = (playbackStatus || source) && (
    <div className="tune-document-metadata" data-testid="tune-metadata">
      {playbackStatus && <span data-testid="tune-playback-status">{playbackStatus}</span>}
      {source && (
        <span className="tune-document-source" data-testid="tune-source-badge">
          {tuneVersionLabel(source)}
        </span>
      )}
    </div>
  );

  // Inside the phone's Tune menu, Love, Suggest less and Report a tune
  // problem are rows of the menu itself, so the details leave them out.
  const renderDetails = (close: () => void, inMenu = false) => (
    <div className="tune-document-form tune-document-details">
      {metadata}
      <label htmlFor={titleId}>Title</label>
      <input
        id={titleId}
        className="text-input"
        aria-label="Chart title"
        autoFocus={!touchFirst()}
        value={score.title}
        onChange={(event) => edit(() => setChartTitle(event.target.value))}
      />
      <label htmlFor={composerId}>Composer</label>
      <input
        id={composerId}
        className="text-input"
        aria-label="Composer"
        placeholder="Composer name"
        value={score.composer ?? ''}
        onChange={(event) => edit(() => setChartComposer(event.target.value))}
      />
      {details}
      <TunePlaybackDefaults onNotice={setNotice} />
      <PlaylistCallControls onNotice={setNotice} />
      {!inMenu && (
        <FeedbackLink kind="tune" onOpen={close}>
          Report a tune problem
        </FeedbackLink>
      )}
      {editError && (
        <p className="tune-document-error" role="alert">
          {editError}
        </p>
      )}
      <button type="button" className="btn btn-sm" onClick={close}>
        Done
      </button>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="tune-document"
      data-testid="tune-document-actions"
      data-save-state={saveState}
    >
      <div className="tune-document-identity">
        <strong className="tune-document-title">{score.title.trim() || 'Untitled tune'}</strong>
        {score.composer && <span className="tune-document-composer">{score.composer}</span>}
        {/* The bar carries no status line (user, Sep 2026: "remove this — it's
            visible on desktop but remove everywhere"): the bar count, the
            corpus badge and "Saved on this device" moved INTO Tune details on
            every width, where the compact layout already kept them. Only a
            save that is NOT happening still speaks up here — that is a
            warning, not status. */}
        {saveWarning && (
          <div className="tune-document-status">
            <span
              className="tune-document-save-state"
              data-testid="tune-save-status"
              data-save-state={saveState}
              role="status"
            >
              {saveWarning}
            </span>
          </div>
        )}
      </div>
      <div className={`tune-document-buttons${compact ? ' tune-document-buttons-compact' : ''}`}>
        {/* Keyed: the two layouts are both lists of popovers, and unkeyed
            React would hand the desktop's open "Save a copy" popover to the
            phone's Tune menu when the width crosses 700px. */}
        {compact ? (
          <Fragment key="compact">
            {/* Where you are in the playlist sits in the header row on a
                phone too (user, Oct 2026), not inside the Tune menu: it is
                the one thing worth knowing at a glance mid-set. */}
            {setControls && setCluster(setControls, 'phone')}
            {/* Add to playlist, worded, in the row in and out of a playlist
                (user, Oct 2026: "Adding a tune to playlist still seems
                hidden"; in a playlist it was gone into the pill's panel). In
                a playlist the row makes room the Apple Music way (the user's
                choice): the pill is the playlist icon alone, Next and Prev
                are in its panel, and Tune is a ⋯ circle; under 375px the
                button is its icon. */}
            {playlistControls && (
              <Popover
                label={
                  <>
                    <ListPlus size={16} aria-hidden="true" />
                    <span className="header-add-label">
                      <span className="visually-hidden">Add to </span>Playlist
                    </span>
                  </>
                }
                title={playlistControls.title}
                testid="header-add-to-playlist"
                align="right"
                overTransport
              >
                {playlistControls.renderPanel}
              </Popover>
            )}
            <Popover
              label={
                setControls ? (
                  <>
                    <MoreHorizontal size={18} aria-hidden="true" />
                    <span className="visually-hidden">Tune</span>
                  </>
                ) : (
                  <>
                    Tune <ChevronDown className="tune-menu-chevron" size={14} aria-hidden="true" />
                  </>
                )
              }
              title="Tune actions"
              testid="tune-menu"
              align="right"
              overTransport
            >
              {(close) => (
                <CompactTuneMenu
                  close={close}
                  closeAndFocus={() => closeTuneMenu(close)}
                  renderDetails={renderDetails}
                  onSaved={copySaved}
                  fileActions={fileActions}
                  copyName={copyName}
                  onCopyNameChange={setCopyNameDraft}
                  playlistControls={playlistControls}
                />
              )}
            </Popover>
          </Fragment>
        ) : (
          <Fragment key="wide">
            {/* First in the row: filing the tune is the action people look
                for most (Oct 2026). */}
            {playlistControls && (
              <Popover
                label={
                  <>
                    <ListPlus className="tune-document-action-icon" size={17} aria-hidden="true" />
                    <span className="tune-document-action-label">Add to playlist</span>
                  </>
                }
                title={playlistControls.title}
                testid="tune-add-to-playlist"
                align="right"
                overTransport
              >
                {playlistControls.renderPanel}
              </Popover>
            )}
            <Popover
              label={
                <>
                  <FilePenLine className="tune-document-action-icon" size={17} aria-hidden="true" />
                  <span className="tune-document-action-label">Tune details</span>
                </>
              }
              title="Tune details"
              testid="edit-details"
              align="right"
              overTransport
            >
              {(close) => renderDetails(close)}
            </Popover>
            <Popover
              label={
                <>
                  <Copy className="tune-document-action-icon" size={17} aria-hidden="true" />
                  <span className="tune-document-action-label">Save a copy</span>
                </>
              }
              title="Save a copy"
              testid="save-tune-copy"
              align="right"
              overTransport
            >
              {(close) => (
                <SaveCopyForm
                  name={copyName}
                  onNameChange={setCopyNameDraft}
                  onSaved={(message) => {
                    copySaved(message);
                    close();
                  }}
                />
              )}
            </Popover>
            {setControls && setCluster(setControls, shortSet ? 'narrow' : 'wide')}
          </Fragment>
        )}
      </div>
      {notice && <Toast testId="tune-document-notice">{notice}</Toast>}
    </div>
  );
}
